import { setOpacity } from "@memorang/helpers/setOpacity";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ImageSourcePropType } from "react-native";
import { ProgressBar, Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { AppLogo } from "../Navigation/AppLogo";

type Props = {
	logo: {
		light: ImageSourcePropType;
		dark: ImageSourcePropType;
	};
	showProgress: boolean;
	progress: number;
	displayStepIndex: number;
	displayStepsTotal: number;
	renderRight?: () => ReactNode;
};

export const OnboardingHeader = ({
	logo,
	showProgress,
	progress,
	displayStepIndex,
	displayStepsTotal,
	renderRight,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { t } = useTranslation();
	const insets = useSafeAreaInsets();
	const stepIndicator = t("onboarding.actions.stepIndicator", {
		step: displayStepIndex,
		total: displayStepsTotal,
	});

	const progressBar = showProgress ? (
		<ProgressBar
			accessibilityLabel={stepIndicator}
			fillStyle={{
				backgroundColor: theme.colors.onPrimary,
			}}
			progress={progress}
			style={{
				height: isMobile ? 4 : undefined,
				borderRadius: isMobile ? 0 : theme.roundness * 99,
				backgroundColor: setOpacity(theme.colors.onPrimary, 0.3),
			}}
		/>
	) : null;

	const stepLabel = showProgress ? (
		<Text variant="labelMedium" style={{ color: theme.colors.onPrimary }}>
			{stepIndicator}
		</Text>
	) : null;

	const appLogo = (
		<AppLogo
			logo={{ default: logo, stacked: logo }}
			inverse
			accessibilityLabel={t("testDriver.appBar.logoAlt")}
			containerStyle={{ width: 146, height: 28 }}
			contentPosition="left"
		/>
	);

	if (isMobile) {
		return (
			<Box backgroundColor={theme.colors.primary} paddingTop={insets.top}>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
					gap={16}
					padding={16}
				>
					{appLogo}
					<Box flexDirection="row" alignItems="center" gap={16}>
						{stepLabel}
						{renderRight?.()}
					</Box>
				</Box>
				{progressBar}
			</Box>
		);
	}

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			gap={16}
			padding={24}
			paddingTop={24 + insets.top}
			backgroundColor={theme.colors.primary}
		>
			{appLogo}
			<Box flex={1}>{progressBar}</Box>
			{stepLabel}
			{renderRight?.()}
		</Box>
	);
};
